import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import {
  adminClientService,
  type InvestorDto,
  type PageResponse,
} from '../../../services/adminClient.service';
import { toast } from 'react-toastify';
import './AdminInvestorList.scss';

export const AdminInvestorList: React.FC = () => {
  const navigate = useNavigate();
  const [investors, setInvestors] = useState<InvestorDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [reactivatingId, setReactivatingId] = useState<number | null>(null);
  const pageSize = 10;

  const loadInvestors = async () => {
    setLoading(true);
    try {
      let result: PageResponse<InvestorDto>;
      if (searchTerm.trim()) {
        result = await adminClientService.searchClients(searchTerm.trim(), page, pageSize);
      } else {
        result = await adminClientService.listClients(page, pageSize);
      }
      setInvestors(result.content || []);
      setTotalPages(result.totalPages || 0);
      setTotalElements(result.totalElements || 0);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load investors');
      setInvestors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvestors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    loadInvestors();
  };

  // Flow-1.pdf point 1: admin reactivation of an account made inoperative on Privacy/Terms withdrawal.
  const handleReactivate = async (inv: InvestorDto) => {
    const name = `${inv.firstName ?? ''} ${inv.lastName ?? ''}`.trim() || inv.emailId || 'this investor';
    if (!window.confirm(`Reactivate ${name}'s account? They will be able to sign in again.`)) return;
    setReactivatingId(inv.id);
    try {
      await adminClientService.reactivate(inv.id);
      toast.success('Account reactivated.');
      await loadInvestors();
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to reactivate account');
    } finally {
      setReactivatingId(null);
    }
  };

  const getStatusBadge = (status?: number) => {
    switch (status) {
      case 1:
        return <span className="badge bg-success">Verified</span>;
      case 2:
        return <span className="badge bg-warning text-dark">Pending</span>;
      case 3:
        return <span className="badge bg-danger">Rejected</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  const inoperativeOnPage = investors.filter((i) => i.accountInoperative).length;

  return (
    <div className="font-sans text-gray-800">
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="dashboard-title-modern mb-0">
          <i className="bi bi-people me-2"></i> Investor Management
        </h1>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3 items-start">
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-3">
          <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wider mb-1">Total Investors</div>
          <div className="text-[24px] font-bold text-[#175cd3] leading-none">{totalElements}</div>
          <div className="text-[11px] text-slate-500 mt-1">In your organization</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-3">
          <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wider mb-1">Inoperative</div>
          <div className="text-[24px] font-bold text-[#b42318] leading-none">{inoperativeOnPage}</div>
          <div className="text-[11px] text-slate-500 mt-1">On this page</div>
        </div>
      </div>

      {/* Search card */}
      <div className="card p-3 mb-3">
        <form onSubmit={handleSearch}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, email, or unique code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="btn btn-primary">
              <i className="bi bi-search me-1"></i> Search
            </button>
          </div>
        </form>
      </div>

      {/* Investors table card */}
      <div className="card p-3">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr className="text-[10px] text-uppercase text-slate-400">
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Unique Code</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {investors.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No investors found
                      </td>
                    </tr>
                  ) : (
                    investors.map((inv) => (
                      <tr key={inv.id}>
                        <td>
                          {inv.firstName} {inv.lastName}
                        </td>
                        <td>{inv.emailId || '—'}</td>
                        <td>{inv.mobilePhone || '—'}</td>
                        <td>{inv.uniqueCode || '—'}</td>
                        <td>
                          {getStatusBadge(inv.verifyStatus)}
                          {inv.accountInoperative && (
                            <span className="badge bg-dark ms-1" title="Privacy Consent / Platform Terms withdrawn">
                              Inoperative
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/admin/clients/${inv.id}`)}
                          >
                            <i className="bi bi-eye me-1"></i> View
                          </button>
                          {inv.accountInoperative && (
                            <button
                              className="btn btn-sm btn-outline-success ms-1"
                              disabled={reactivatingId === inv.id}
                              onClick={() => handleReactivate(inv)}
                            >
                              <i className="bi bi-unlock me-1"></i>
                              {reactivatingId === inv.id ? 'Reactivating…' : 'Reactivate'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <nav className="mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted small">
                    Showing {page * pageSize + 1} to{' '}
                    {Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
                  </span>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        Previous
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">
                        Page {page + 1} of {totalPages}
                      </span>
                    </li>
                    <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};
